import Immutable from "immutable";
import { seriesToMetrics } from "views/components/aggregationwizard/metric/MetricElement";
import { concatQueryStrings, escape, formatValueForQuery } from "views/logic/queries/QueryHelper";
import { fieldValueOrClause } from "views/logic/valueactions/ValueActionQueryHelper";
const scalarValue = (value) => Array.isArray(value) ? value.join(",") : value;
const searchFromValue = ({
  field,
  value,
  type
}) => {
  if (!Array.isArray(value)) {
    return `${field}:${formatValueForQuery(value, type)}`;
  }
  if (value.length === 0) {
    return "";
  }
  if (value.length === 1) {
    return `${field}:${formatValueForQuery(value[0], type)}`;
  }
  return fieldValueOrClause(
    field,
    value.map((arrayValue) => ({ value: arrayValue, type }))
  );
};
export const getStreams = (filter) => {
  if (!filter) return [];
  return filter.get("filters").filter((curFilter) => curFilter.get("type") === "stream").map((curFilter) => curFilter.get("id")).toArray();
};
export const transformValuePathToQuery = (valuePath) => concatQueryStrings(
  valuePath.filter((path) => {
    const key = Object.keys(path)[0];
    return key !== "_exists_";
  }).map((path) => {
    const [field, value] = Object.entries(path)[0];
    return `${field}:${escape(value)}`;
  }),
  { withBrackets: false }
);
export const getFlattenPivots = (pivots) => new Set(pivots.flatMap(({ fields }) => fields));
export const filtratePathsByPivot = ({
  flattenPivots,
  valuePath
}) => {
  if (!valuePath) return [];
  const map = valuePath.reduce((res, cur) => {
    const key = Object.keys(cur)[0];
    if (!res.has(key) && flattenPivots.has(key)) {
      res.set(key, cur);
    }
    return res;
  }, /* @__PURE__ */ new Map([]));
  return Array.from(map.values());
};
export const aggregationMetricValueHandler = ({ widget, value, field, valuePath }) => {
  const curSeries = widget.config.series.find((series) => series.function === field);
  const { field: agg_field, function: agg_function } = seriesToMetrics([curSeries])[0];
  const { rowPivots, columnPivots } = widget.config;
  const flattenRowPivots = getFlattenPivots(rowPivots);
  const flattenColumnPivots = getFlattenPivots(columnPivots);
  const rowPaths = filtratePathsByPivot({ flattenPivots: flattenRowPivots, valuePath });
  const columnPaths = filtratePathsByPivot({ flattenPivots: flattenColumnPivots, valuePath });
  const rowValuePath = transformValuePathToQuery(rowPaths);
  const columnValuePath = transformValuePathToQuery(columnPaths);
  return {
    aggField: agg_field,
    aggFunction: agg_function,
    aggValue: scalarValue(value),
    rowGroupBy: Array.from(flattenRowPivots),
    columnGroupBy: Array.from(flattenColumnPivots),
    rowValuePath,
    columnValuePath
  };
};
export const aggregationValueHandler = ({ widget, value, field, valuePath, type }) => {
  const { rowPivots } = widget.config;
  const flattenRowPivots = getFlattenPivots(rowPivots);
  const rowPaths = filtratePathsByPivot({ flattenPivots: flattenRowPivots, valuePath });
  const rowValuePath = transformValuePathToQuery(rowPaths);
  return {
    searchFromValue: searchFromValue({ field, value, type }),
    rowValuePath
  };
};
export const messagesValueHandler = ({ value, field, type }) => ({
  searchFromValue: searchFromValue({ field, value, type })
});
export const logsValueHandler = ({ value, field, type }) => ({
  searchFromValue: searchFromValue({ field, value, type })
});
export const getAggregationHandler = ({ widget, field }) => {
  if (widget.type === "AGGREGATION") {
    const isMetrics = !!widget.config.series.find((series) => series.function === field);
    return isMetrics ? aggregationMetricValueHandler : aggregationValueHandler;
  }
  if (widget.type === "logs") return logsValueHandler;
  if (widget.type === "messages") return messagesValueHandler;
  throw new Error("This widget type has incorrect type or no handler");
};
export const getLutParameters = (parameters) => parameters.reduce((res, cur) => {
  if (cur.type === "lut-parameter-v1") {
    const paramJSON = cur.toJSON();
    res.push(paramJSON);
  }
  return res;
}, []);
export const getRestParameterValues = ({
  parameters,
  parameterBindings
}) => Object.fromEntries(
  parameters.filter((cur) => cur.type !== "lut-parameter-v1").map((cur) => {
    var _a;
    const paramJSON = cur.toJSON();
    const { name } = paramJSON;
    const bindingValue = (_a = parameterBindings == null ? void 0 : parameterBindings.get(name)) == null ? void 0 : _a.value;
    return [name, bindingValue != null ? bindingValue : paramJSON == null ? void 0 : paramJSON.default_value];
  }).toArray()
);
export const transformSearchFiltersToQuery = (filters = Immutable.List([])) => concatQueryStrings(
  filters.filter((filter) => filter.queryString && !filter.disabled).map((filter) => `${filter.negation ? "NOT" : ""}(${filter.queryString})`).toArray(),
  { withBrackets: false }
);
export const replaceParametersInQueryString = ({
  query,
  restParameterValues
}) => {
  let curQuery = query;
  Object.entries(restParameterValues).forEach(([parameterName, parameterValue]) => {
    curQuery = curQuery.replace(`$${parameterName}$`, `${parameterValue}`);
  });
  return curQuery;
};
